import { useState } from 'react';
import { format } from 'date-fns';
import { CalendarDays } from 'lucide-react';
import type { DateRange as DayRange } from 'react-day-picker';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import {
  DATE_PRESETS, dateFieldChoices, presetRange,
  type ChipDate, type ChipDateField, type DateRange, type SearchEntity,
} from '@/lib/search/searchFilterChips';
import { cn } from '@/lib/utils';

const FIELD_LABEL: Record<ChipDateField, string> = {
  active: 'Active during', created: 'Created in', activity: 'Activity in', occurred: 'Occurred in', event: 'Event in',
};

const toLocalDate = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};
const toIso = (d: Date) => format(d, 'yyyy-MM-dd');
const nextDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
const prevDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1);

interface PickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The date filter in effect (typed or picked), shown as the selection. */
  value?: ChipDate;
  entity?: SearchEntity;
  timeZone: string;
  onPick: (range: DateRange) => void;
  onField: (field: ChipDateField) => void;
  onClear?: () => void;
  /** Where focus goes when the picker closes (e.g. the date chip that opened it). */
  onCloseAutoFocus?: (event: Event) => void;
}

/**
 * The calendar glyph at the right of the search box (spec: always available).
 * Presets resolve through the query grammar, so "Last month" here is exactly
 * what typing "last month" gives. A custom range is picked on the calendar and
 * applied as local dates, half-open like every other date filter.
 */
export function SearchDateRangePicker(props: PickerProps) {
  const { open, onOpenChange } = props;
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Filter by date"
          title="Filter by date"
          className={cn(
            'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#8FB560]',
            props.value && 'text-foreground',
          )}
        >
          <CalendarDays className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" aria-label="Date range" onCloseAutoFocus={props.onCloseAutoFocus} className="w-auto p-0">
        {/* Remounts on every open, so the draft starts from the filter in effect. */}
        <PickerBody {...props} />
      </PopoverContent>
    </Popover>
  );
}

function PickerBody({ value, entity, timeZone, onPick, onField, onClear, onOpenChange }: PickerProps) {
  const narrow = useMediaQuery('(max-width: 639.98px)');
  // Presets resolve against the clock when the picker OPENS (it remounts each
  // time), not when the header mounted: a tab left open overnight must not
  // offer yesterday as "Today".
  const [openedAt] = useState(() => new Date());
  // `to` is exclusive; the calendar shows the last day inside the range.
  const [draft, setDraft] = useState<DayRange | undefined>(() => (
    value ? { from: toLocalDate(value.from), to: prevDay(toLocalDate(value.to)) } : undefined
  ));
  const fields = dateFieldChoices(entity);
  const presets = DATE_PRESETS.map(([label, phrase]) => ({ label, range: presetRange(phrase, openedAt, timeZone) }));

  const pick = (range: DateRange) => {
    onPick(range);
    onOpenChange(false);
  };
  const apply = () => {
    if (!draft?.from) return;
    pick({ from: toIso(draft.from), to: toIso(nextDay(draft.to ?? draft.from)) });
  };

  return (
    <div className="flex flex-col sm:flex-row">
      <div role="group" aria-label="Date presets" className="flex flex-row flex-wrap gap-1 border-b border-border p-2 sm:w-36 sm:flex-col sm:flex-nowrap sm:border-b-0 sm:border-r">
        {presets.map(({ label, range }) => {
          const selected = Boolean(range && value && range.from === value.from && range.to === value.to);
          return (
            <button
              key={label}
              type="button"
              disabled={!range}
              aria-pressed={selected}
              onClick={() => range && pick(range)}
              className={cn(
                'rounded px-2 py-1.5 text-left text-sm text-foreground hover:bg-accent',
                'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#8FB560]',
                selected && 'bg-accent',
              )}
            >
              {label}
            </button>
          );
        })}
      </div>
      <div>
        {fields.length > 1 && (
          <div role="group" aria-label="Date means" className="flex items-center gap-1 px-3 pt-3">
            {fields.map((f) => (
              <button
                key={f}
                type="button"
                aria-pressed={value?.field === f}
                onClick={() => onField(f)}
                className={cn(
                  'rounded px-2 py-1 text-xs',
                  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#8FB560]',
                  value?.field === f ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {FIELD_LABEL[f]}
              </button>
            ))}
          </div>
        )}
        <Calendar
          mode="range"
          selected={draft}
          onSelect={setDraft}
          defaultMonth={draft?.from}
          numberOfMonths={narrow ? 1 : 2}
        />
        <div className="flex justify-end gap-2 border-t border-border p-3">
          {onClear && (
            <Button variant="ghost" size="sm" onClick={() => { onClear(); onOpenChange(false); }}>
              Clear date
            </Button>
          )}
          <Button size="sm" disabled={!draft?.from} onClick={apply}>
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
}
